import { useEffect, useState } from 'react';

import {
  GearbeltEvent,
  GetCurrentWebUserResponseDTO,
  ProductsInitials,
  SignUpUserInfoNextActionEventContext,
  SignupUtmMetadata,
  WorkspaceDTO,
} from '@gearbelt/global';
import { zodResolver } from '@hookform/resolvers/zod';
import { SelectorTypes } from '@site-mate/sitemate-global-shared';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { jwtDecode } from 'jwt-decode';
import { HTTPError } from 'ky';
import { User } from 'oidc-client-ts';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useForm } from 'react-hook-form';
import { Toaster } from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { useToast } from '@/components/ui/toaster';
import { env } from '@/environment';
import { userManager } from '@/lib/fusionauth';
import { cn } from '@/lib/utils';
import { ApiService } from '@/services/api-v2';
import { workspaceService } from '@/services/global-api/workspace.service';
import { useSignUpReferralStore } from '@/stores/signup-referral.store';
import { SignUpSteps, useSignUpStore } from '@/stores/signup.store';
import { useUserStore } from '@/stores/user.store';
import { setForceLogoutFlagForSSOSignUp } from '@/utils/auth';
import { useEvents } from '@/utils/events';
import { useWorkspaceNavigate } from '@/utils/routes';

import { BusinessAttributesSelectorStep } from './business-attributes/business-attributes-selector-step';
import { PhoneVerificationStep } from './sign-up-phone-verification';
import { SignUpConstructionBackground } from './signup-construction-background';
import { emailSchema, SignUpFormData, signUpFormSchema } from './signup-form-utils';
import { maskPhone } from './signup-otp-utils';
import { SignUpSidebarBackground } from './signup-sidebar-background';
import { EmailAndUserInfoStep, HumanVerificationStep, MobileNumberStep } from './signup-steps';
import { useSignupOtpFlow } from './use-signup-otp-flow';
import { UsedDomainStep } from './used-domain-step';
import { UsedEmailStep } from './used-email-step';

export function ReCAPTCHAWrappedSignUpPage() {
  return (
    <GoogleReCaptchaProvider reCaptchaKey={env.recaptcha.v3SiteKey}>
      <SignUpPage />
    </GoogleReCaptchaProvider>
  );
}

function SignUpPage() {
  const { signUpStep, setSignUpStep, setSignUpForm, utmMetadata, setUtmMetadata } = useSignUpStore();
  const { signUpReference, referenceSmsAccountId, setSignUpReference } = useSignUpReferralStore();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    setSignUpStep(SignUpSteps.Email);
  }, [setSignUpStep]);

  useEffect(() => {
    const utmMetadata: SignupUtmMetadata = {
      templateId: searchParams.get('templateId'),
      utm_medium: searchParams.get('utm_medium'),
      utm_source: searchParams.get('utm_source'),
      utm_campaign: searchParams.get('utm_campaign'),
      utm_term: searchParams.get('utm_term'),
      utm_content: searchParams.get('utm_content'),
      _gl: searchParams.get('_gl'),
      gclid: searchParams.get('gclid'),
    };

    setUtmMetadata(utmMetadata);

    // Capture reference param separately (not part of utmMetadata)
    if (searchParams.get('reference')) {
      setSignUpReference(searchParams.get('reference') || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toast = useToast();
  const navigate = useWorkspaceNavigate();
  const { user, org, setUser, setOrg } = useUserStore();
  const [loading, setLoading] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [useReCAPTCHAV2, setUseReCAPTCHAV2] = useState(false);
  const [reCAPTCHAV2Token, setReCAPTCHAV2Token] = useState<string | null>(null);
  const [hasEmailError, setHasEmailError] = useState(false);
  const form = useForm<SignUpFormData>({
    resolver: zodResolver(signUpFormSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      workspaceName: '',
      password: '',
      businessAttributes: { [SelectorTypes.Vertical]: null, [SelectorTypes.Role]: null, [SelectorTypes.Discipline]: null },
      mobile: '',
      timezone: dayjs.tz.guess(),
      continent: '',
      isSSOSignUp: false,
    },
    mode: 'onChange',
  });

  const isSSOSignUp = form.watch('isSSOSignUp');

  const { trackEvent } = useEvents();
  useEffect(() => {
    trackEvent(GearbeltEvent.SignUpPageVisited, {});
  }, []);

  useEffect(() => {
    if (org?.workspaceId) {
      navigate(`/dashboard?isSSOSignup=${isSSOSignUp}`);
    }
  }, [org, navigate]);

  async function checkIsEmailInUse(email: string): Promise<ProductsInitials | null> {
    try {
      await ApiService.ValidateUniqueUserEmail({ email });
    } catch (error) {
      if (error instanceof HTTPError) {
        if (error.response.status === 409) {
          return error.response.json();
        } else {
          setLoading(false);
          toast.error(await error.response.json());
          throw error;
        }
      }
    }
    return null;
  }

  async function checkIsBusinessDomainInUse() {
    const email = form.getValues().email;
    const domain = email.split('@')[1];

    const { isBusinessDomainInUse } = await workspaceService.isBusinessDomainInUse(domain);
    return isBusinessDomainInUse;
  }

  async function onEmailStepNext() {
    setLoading(true);
    const email = form.getValues().email;
    const emailValidation = emailSchema.safeParse(email);

    if (!emailValidation.success) {
      setHasEmailError(true);
      setLoading(false);
      return;
    }

    const isEmailInUse: ProductsInitials | null = await checkIsEmailInUse(email);
    const isBusinessDomainInUse = await checkIsBusinessDomainInUse();
    const usedEmailSteps = {
      [ProductsInitials.GB]: SignUpSteps.UsedGearbeltEmail,
      [ProductsInitials.SM]: SignUpSteps.UsedSitemateEmail,
    };

    const nextStep = isEmailInUse ? usedEmailSteps[isEmailInUse] : isBusinessDomainInUse ? SignUpSteps.UsedDomain : SignUpSteps.UserInfo;

    let signupContext: SignUpUserInfoNextActionEventContext;
    // Event tracking
    if (isEmailInUse) {
      signupContext = SignUpUserInfoNextActionEventContext.Existing;
    } else if (isBusinessDomainInUse) {
      signupContext = SignUpUserInfoNextActionEventContext.DomainCheck;
    } else {
      signupContext = SignUpUserInfoNextActionEventContext.New;
      trackEvent(GearbeltEvent.CTAClicked, {
        Context: 'Sign Up Get Started',
      });
    }
    trackEvent(GearbeltEvent.SignUpUserEmail, {
      Context: signupContext,
    });

    if ([SignUpSteps.UserInfo, SignUpSteps.UsedDomain].includes(nextStep)) {
      await verifySSODomain(email);
    }
    setNextSignUpStep(nextStep);
    setLoading(false);
  }

  async function onUserInfoStepNext() {
    trackEvent(GearbeltEvent.CTAClicked, {
      Context: 'Sign Up Page 1',
    });

    setNextSignUpStep(SignUpSteps.BusinessAttributes);
  }

  async function verifySSODomain(email: string) {
    const domain = email.split('@')[1];
    const { isSSODomain } = await workspaceService.isSSODomain(domain);
    if (isSSODomain) {
      form.setValue('password', '');
    }
    form.setValue('isSSOSignUp', isSSODomain);
  }

  function setNextSignUpStep(nextStep: SignUpSteps) {
    setSignUpStep(nextStep);
    const formValues = form.getValues();
    formValues.password = '';
    setSignUpForm({ ...formValues });
  }

  const {
    otpStatus,
    otpErrorReason,
    otpCooldownSeconds,
    verificationTokenRef,
    onMobileNumberNext,
    onOtpVerify,
    onOtpResend,
    onOtpRequestCall,
    resetForNewNumber,
  } = useSignupOtpFlow({
    getPhone: () => form.getValues().mobile,
    persistForm: () => setSignUpForm(form.getValues()),
    onSubmit,
    executeRecaptcha,
    trackEvent,
    setSignUpStep,
    setLoading,
    showErrorToast: (message) => toast.error(message),
  });

  // The v2 challenge only resumes the account-creation submission; the OTP-send path no longer routes here.
  async function onHumanVerificationNext() {
    await onSubmit();
  }

  const { mutateAsync: signup } = useMutation({
    mutationFn: ({
      recaptchaVersion,
      recaptchaToken,
      ...values
    }: z.infer<typeof signUpFormSchema> & {
      recaptchaToken: string;
      recaptchaVersion: string;
    }) => {
      const placeholderPassword = 'SSOSignup123';
      if (values.isSSOSignUp) {
        values.password = placeholderPassword;
      }
      return ApiService.SignUp({
        ...values,
        recaptchaToken: recaptchaToken,
        recaptchaSiteKey: recaptchaVersion === 'v2' ? env.recaptcha.v2SiteKey : env.recaptcha.v3SiteKey,
        recaptchaAction: 'signup',
        signUpReference,
        referenceSmsAccountId,
        utmMetadata,
        ...(verificationTokenRef.current ? { verificationToken: verificationTokenRef.current } : {}),
      });
    },

    onSuccess: async (result) => {
      const { loginResult, user } = await result.json();
      const oidcUser = new User({
        token_type: 'Bearer',
        access_token: loginResult.accessToken,
        expires_at: loginResult.accessTokenExpiration,
        refresh_token: loginResult.refreshToken,
        profile: jwtDecode(loginResult.accessToken),
      });

      if (userManager) {
        await userManager.storeUser(oidcUser);
      }

      userManager.events.load(oidcUser);
      setUser({ ...user } as unknown as GetCurrentWebUserResponseDTO);
      setOrg(user.workspaces[0] as unknown as WorkspaceDTO);
      if (isSSOSignUp) {
        setForceLogoutFlagForSSOSignUp();
      }
      setSignUpStep(SignUpSteps.WorkspaceBranding);
    },

    onError: async (error: HTTPError) => {
      const errorMessage = await error.response?.json<{ message: string }>().then((json) => json.message);
      if (errorMessage === 'reCAPTCHA verification failed') {
        trackEvent(GearbeltEvent.SignUpHumanVerificationRequired, {});
        return;
      }
      toast.error(`Signup error: ${errorMessage}`);
    },
  });

  async function triggerRecaptchaV3(token: string) {
    try {
      setLoading(true);

      await signup({
        ...form.getValues(),
        recaptchaVersion: 'v3',
        recaptchaToken: token,
      });
    } catch (error) {
      setUseReCAPTCHAV2(true);
      setSignUpStep(SignUpSteps.HumanVerification);
    } finally {
      setLoading(false);
    }
  }

  async function triggerRecaptchaV2(token: string) {
    try {
      setLoading(true);

      await signup({
        ...form.getValues(),
        recaptchaVersion: 'v2',
        recaptchaToken: token,
      });
    } catch (error) {
      if (error instanceof Error) {
        toast.error(error.message);
      }
    } finally {
      setLoading(false);
    }
  }

  async function onSubmit() {
    if (!useReCAPTCHAV2 && executeRecaptcha) {
      const reCAPTCHAV3Token = await executeRecaptcha('signup');
      await triggerRecaptchaV3(reCAPTCHAV3Token);
    } else if (useReCAPTCHAV2 && reCAPTCHAV2Token) {
      await triggerRecaptchaV2(reCAPTCHAV2Token);
    } else {
      toast.error('Please complete the reCAPTCHA');
    }
  }
  return (
    <>
      {[SignUpSteps.Email, SignUpSteps.UserInfo].includes(signUpStep) && (
        <>
          <SignUpConstructionBackground>
            <EmailAndUserInfoStep
              form={form}
              loading={loading}
              onNextStep={signUpStep === SignUpSteps.Email ? onEmailStepNext : onUserInfoStepNext}
              register={form.register}
              hasEmailError={hasEmailError}
            />
          </SignUpConstructionBackground>

          <SignUpFooter className="absolute bottom-4 left-1/2 -translate-x-1/2" />
        </>
      )}

      {signUpStep === SignUpSteps.BusinessAttributes && (
        <SignUpSidebarBackground>
          <BusinessAttributesSelectorStep<SignUpFormData>
            form={form}
            loading={loading}
            register={form.register}
            onNextStep={() => {
              setNextSignUpStep(SignUpSteps.MobileNumber);
              setSignUpForm(form.getValues());
            }}
          />
        </SignUpSidebarBackground>
      )}

      {signUpStep === SignUpSteps.MobileNumber && (
        <SignUpSidebarBackground>
          <MobileNumberStep form={form} loading={loading} register={form.register} onNextStep={onMobileNumberNext} />
        </SignUpSidebarBackground>
      )}
      {signUpStep === SignUpSteps.PhoneVerification && (
        <SignUpSidebarBackground>
          <PhoneVerificationStep
            status={otpStatus}
            errorReason={otpErrorReason}
            serverCooldownSeconds={otpCooldownSeconds}
            maskedPhone={maskPhone(form.getValues().mobile)}
            onVerify={onOtpVerify}
            onResend={onOtpResend}
            onRequestCall={onOtpRequestCall}
            onUseDifferentNumber={resetForNewNumber}
          />
        </SignUpSidebarBackground>
      )}
      {signUpStep === SignUpSteps.HumanVerification && (
        <SignUpSidebarBackground>
          <HumanVerificationStep
            form={form}
            loading={loading}
            register={form.register}
            setReCAPTCHAV2Token={setReCAPTCHAV2Token}
            onNextStep={onHumanVerificationNext}
          />
        </SignUpSidebarBackground>
      )}
      {(signUpStep === SignUpSteps.UsedGearbeltEmail || signUpStep === SignUpSteps.UsedSitemateEmail) && (
        <>
          <SignUpConstructionBackground>
            <UsedEmailStep />
          </SignUpConstructionBackground>
          <SignUpFooter className="absolute bottom-4 left-1/2 -translate-x-1/2" />
        </>
      )}
      {signUpStep === SignUpSteps.UsedDomain && (
        <>
          <SignUpConstructionBackground>
            <UsedDomainStep />
          </SignUpConstructionBackground>
          <SignUpFooter className="absolute bottom-4 left-1/2 -translate-x-1/2" />
        </>
      )}

      <Toaster />
    </>
  );
}

export function SignUpFooter({ className }: { className?: string }) {
  return (
    <footer className={cn('text-secondary-text text-sm', className)}>
      Powered by{' '}
      <a className="pl-0.5 font-semibold hover:underline" href="https://sitemate.com" rel="noopener noreferrer">
        Sitemate
      </a>
    </footer>
  );
}
